import { type ApiID, type Nullable, type TempApiID, type UUID } from '@/model/Common.model';
import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';

type IsApiIdResponse<T extends string, AllowTemp extends boolean> = AllowTemp extends true
  ? ApiID<T> | TempApiID<T>
  : ApiID<T>;

type ChangeApiIdPrefixResponse<T extends string, IsTemp extends boolean> = IsTemp extends true
  ? TempApiID<T>
  : ApiID<T>;

const base62Chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const hyphenPattern = /-/g;
const looseApiIdPattern = /^(?<isTemp>~)?(?<prefix>[a-z]{3})_(?<id>[a-zA-Z0-9]{10,22})$/;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const apiIdPattern = /^(?<entityType>[a-z]{3})_(?<id>[a-zA-Z0-9]{10,22})$/;

const bigIntToBase62 = (bigInt: bigint): string => {
  if (bigInt === 0n) {
    return '0';
  }
  let result = '';
  let num = bigInt;
  while (num > 0n) {
    const remainder = num % 62n;
    result = base62Chars[Number(remainder)] + result;
    num = num / 62n;
  }
  return result;
};

const base62ToBigInt = (base62: string): bigint => {
  let result = 0n;
  for (let i = 0; i < base62.length; i++) {
    const char = base62[i];
    const value = base62Chars.indexOf(char);
    if (value === -1) {
      throw new InVivoError('Invalid base62 character', {
        slug: 'api-id-decode',
        context: { character: char, source_api_id: base62 },
      });
    }
    result = result * 62n + BigInt(value);
  }
  return result;
};

const bigIntToHex = (bigInt: bigint): string => {
  if (bigInt === 0n) {
    return '0';
  }
  let result = '';
  let num = bigInt;
  while (num > 0n) {
    const remainder = num % 16n;
    result = remainder.toString(16) + result;
    num = num / 16n;
  }
  return result;
};

const formatAsUUID = (hex: string): UUID => {
  const paddedHex = hex.padStart(32, '0');
  return `${paddedHex.slice(0, 8)}-${paddedHex.slice(8, 12)}-${paddedHex.slice(12, 16)}-${paddedHex.slice(16, 20)}-${paddedHex.slice(20, 32)}`;
};

export const encodeUUID = (uuid: UUID): string => {
  if (isUUID(uuid)) {
    return bigIntToBase62(
      Array.from(uuid.replace(hyphenPattern, '').toLowerCase()).reduce(
        (acc, hexDigit) => acc * 16n + BigInt(parseInt(hexDigit, 16)),
        0n
      )
    );
  }
  throw new InVivoError(`Invalid uuid`, { slug: 'encode-uuid', context: { uuid } });
};

export const decodeApiId = (apiId: ApiID): string => {
  const match = looseApiIdPattern.exec(apiId);
  if (_notNil(match) && _notNil(match?.groups?.id)) {
    return formatAsUUID(bigIntToHex(base62ToBigInt(match.groups.id)));
  }
  throw new InVivoError('Cannot decode API ID', { slug: 'api-id-decode', context: { source_api_id: apiId } });
};

export const generateApiId = <T extends string>(prefix: T, uuid?: UUID): ApiID<T> => {
  return `${prefix}_${encodeUUID(uuid ?? crypto.randomUUID())}`;
};
export const generateTempApiId = <T extends string>(prefix: T, uuid?: UUID): TempApiID<T> => {
  return generateApiId(`~${prefix}`, uuid);
};

export const isUUID = (value: Nullable<string>): value is UUID => {
  return uuidPattern.test(value ?? '');
};

export const isApiId = <T extends string, AllowTemp extends boolean = false>(
  prefix: T,
  value: Nullable<string>,
  allowTemporaryApiIds?: AllowTemp
): value is IsApiIdResponse<T, AllowTemp> => {
  const match = looseApiIdPattern.exec(value ?? '');
  return (
    _notNil(match) &&
    match.groups?.prefix === prefix &&
    !(_notNil(match.groups?.isTemp) && !(allowTemporaryApiIds ?? false) === true)
  );
};
export const isTempApiId = <T extends string, AllowTemp extends boolean = false>(
  prefix: T,
  value: Nullable<string>
): value is TempApiID<T> => {
  const match = looseApiIdPattern.exec(value ?? '');
  return _notNil(match) && match.groups?.prefix === prefix && _notNil(match.groups?.isTemp);
};

export const changeApiIdPrefix = <T extends string, IsTemp extends boolean>(
  sourceApiId: ApiID,
  targetPrefix: T,
  temp?: IsTemp
): ChangeApiIdPrefixResponse<T, IsTemp> => {
  const match = looseApiIdPattern.exec(sourceApiId);
  if (_notNil(match)) {
    return `${temp ? '~' : ''}${targetPrefix}_${match.groups?.id}` as ChangeApiIdPrefixResponse<T, IsTemp>;
  }
  throw new InVivoError(`Cannot change api id prefix as ${sourceApiId} is not valid`, {
    slug: 'change-api-id-prefix',
    context: { sourceApiId, targetPrefix },
  });
};
